import { Outlet, useLocation } from "react-router-dom";

import { Collapse, Divider, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { DesktopOnlyActionBar } from "@app/analytics/components/ActionBar";
import DateSelector from "@app/analytics/components/DateSelector";
import SearchBar from "@app/analytics/components/SearchBar/SearchBar";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import useUploadStore from "@app/analytics/upload/useUploadStore";
import { getDateRanges } from "@app/analytics/utils";
import ROUTES from "@app/common/routes";

import { Link } from "@rui/Link";
import Stack, { MobileOnlyColumn, Row } from "@rui/Stack";
import { BackButton } from "@rui/styles";

import usePopulateReportListMetadata from "./hooks/usePopulateReportListMetadata";
import useUnseenReports from "./hooks/useUnseenReports";
import MeetingReportsContainer from "./MeetingReportsContainer";
import MeetingReportsTabs from "./MeetingReportsTabs";
import SearchFilters from "./SearchResults/SearchFilters";
import { StickyReportsHeader } from "./styles";

const ActionBarSection = styled(Row)({
  flex: 1,
  alignItems: "center",
});

const MeetingReports = () => {
  const location = useLocation();

  const openUploadModal = useUploadStore((state) => state.openModal);
  const dateRanges = useDateRangeStore(getDateRanges);

  // Must call these hooks so unseen meeting counts are calculated and tags are populated on initial load
  useUnseenReports({ dateRanges });
  usePopulateReportListMetadata({ dateRange: dateRanges[0] });

  const viewingSearchResults = location.pathname.includes(
    ROUTES.MEETING_REPORTS_SEARCH
  );

  return (
    <Stack sx={{ width: "100%", height: "100%", bgcolor: "white", pb: 3 }}>
      <StickyReportsHeader sx={{ bgcolor: "#FFF" }}>
        <DesktopOnlyActionBar spacing={1.5} sx={{ height: "fit-content" }}>
          <ActionBarSection sx={{ minWidth: "fit-content" }}>
            <Row variant="left" spacing={1}>
              <Collapse
                in={viewingSearchResults}
                unmountOnExit
                orientation="horizontal"
              >
                <Row variant="left" spacing={1.5}>
                  <Link to={ROUTES.MEETING_REPORTS}>
                    <BackButton />
                  </Link>
                  <Divider orientation="vertical" sx={{ height: "24px" }} />
                </Row>
              </Collapse>
              <Typography variant="h2">
                {viewingSearchResults ? "Search" : "Reports"}
              </Typography>
            </Row>
          </ActionBarSection>
          <ActionBarSection>
            <SearchBar />
          </ActionBarSection>
          <ActionBarSection variant="right" sx={{ minWidth: "fit-content" }}>
            <DateSelector />
          </ActionBarSection>
        </DesktopOnlyActionBar>
        {viewingSearchResults ? (
          <Stack sx={{ mx: 3 }}>
            <MobileOnlyColumn paddingTop={1}>
              <SearchBar />
              <Divider sx={{ marginTop: 1, marginX: -4 }} />
            </MobileOnlyColumn>
            <SearchFilters />
            <Divider sx={{ marginX: { xxs: -4, sm: 0 } }} />
          </Stack>
        ) : (
          <MeetingReportsTabs onUploadClick={openUploadModal} />
        )}
      </StickyReportsHeader>
      <MeetingReportsContainer>
        <Outlet />
      </MeetingReportsContainer>
    </Stack>
  );
};

export default MeetingReports;
